/*
 * Copyright (c) 2000-2022 TeamDev. All rights reserved.
 * TeamDev PROPRIETARY and CONFIDENTIAL.
 * Use is subject to license terms.
 */

import {Vue, Component} from 'vue-property-decorator';
import VueI18n from 'vue-i18n';

/**
 * A generic base class for components, which can be used instead of the standard `Vue` class.
 * This is a place that contains widely used extensions that simplify component authoring.
 */
@Component
export class LservVue extends Vue {

  /**
   * An analog of the `$t()` method (coming rom the Vue I18n plugin) that uses a relative
   * resource path.
   *
   * This method assumes that the `i18nBasePath` property getter is overridden by this component.
   * The difference from the `$t()` method is that the resource key passed to this `t()` method is
   * relative to the path specified in the `i18nBasePath` property.
   *
   * The resulting resource key is calculated as ` ``lserv-app.${i18nBasePath}.$[relativeKey}`` `.
   *
   * See also the [Localization Resources Structure](../../Design-and-Conventions.md#Localization Resources Structure)
   * section of the [Design-and-Conventions.md](../../Design-andConventions.md#Localization Resources Structure) document.
   *
   * @param relativeKey  .json key relative to the component's path specified in `i18nBasePath`
   * @return  resource string (or object) at the specified path
   */
  protected t(
      relativeKey: string,
      values?: VueI18n.Values
  ): VueI18n.TranslateResult | string {
    const base = this.getI18nBase();
    if (!base) {
      return this.$t(relativeKey, values);
    }
    return this.$t(`lserv-app.${base}.${relativeKey}`, values);
  }

  /**
   * Similar to the `$tc()) method from the Vue I18 plugin, but accepts a relative key, like
   * in the `t()` method)
   *
   * @param relativeKey  .json key relative to the component's path specified in `i18nBasePath`
   * @param num          a number that is used to resolve the specified localization entry
   * @return  the respective resource string at the specified path
   */
  protected tc(relativeKey: string, num: number): string {
    const base = this.getI18nBase();
    return this.$tc(`lserv-app.${base ? base + '.' : ''}${relativeKey}`, num);
  }

  /**
   * Expected to be overridden by each component that uses the `t()` function to return a base
   * i18n resource path that contains the component's own resources.
   *
   * NOTE: it should specify a path:
   * - without the leading `'lserv-app.'` prefix (since it's common for the entire application), and...
   * - without a trailing dot.
   *
   * An empty string means that all `t()` method calls will essentially fetch i18n strings by
   * absolute paths (relative to the root .json node).
   */
  protected getI18nBase(): string {
    return '';
  }
}
